import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { TvLoginStore } from '../../pages/app/services/app.store';
import { hostConfig } from '../common.model';
import { RouterStates } from '../constants/router-states.constant';
import { ClientSSO } from '../entities/client-sso';
import { EErrorCode } from '../entities/error-code';
import { LoginResponse } from '../entities/login-response';
import { StoreService } from '../stores/store.service';
import { AppTranslateService } from './app-translate.service';
import { DictionaryAPIService } from './dictionary-api.service';
import { LanguageService } from './languages.service';
import { MobileService } from './mobile.service';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  readonly #tvLoginStore = inject(TvLoginStore);
  private MAIN_APP_URL = hostConfig.monolithApi + '/cloud';
  private LOGIN_AZURE = `${hostConfig.loginApi}/azure-ad`;
  private LOGIN_CIAM = `${hostConfig.loginApi}/ciam-azure-ad`;
  private readonly SSO_LOGIN_CALLBACK = 'isSSOLoginCallBack';

  constructor(
    private router: Router,
    private store: StoreService,
    private languageService: LanguageService,
    private appTranslateService: AppTranslateService,
    private mobileService: MobileService,
    private titleService: Title,
    private translate: TranslateService,
    private dictionaryAPIService: DictionaryAPIService
  ) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => this.onRouteChangeSuccess(event));
  }

  redirectToAzure(): void {
    const returnUrl = this.#tvLoginStore.getUrlParameterReturnUrl();
    window.location.href = this.LOGIN_AZURE + (returnUrl ? '?returnUrl=' + encodeURIComponent(returnUrl) : '');
  }

  redirectToCiam(): void {
    const url = new URL(this.LOGIN_CIAM);
    const params = url.searchParams;

    const returnUrl = this.#tvLoginStore.getUrlParameterReturnUrl();

    if (returnUrl) {
      params.set('returnUrl', returnUrl);
      params.set('clientAlias', this.store.getLoginAlias());
    }

    if (this.store.client.b2CAccessOnly) {
      params.set('domain_hint', this.store.client.b2CDomainHint);
    } else {
      const email = this.#tvLoginStore.email();
      if (email) params.set('email', email);
    }

    this.redirectTo(url.toString());
  }

  goToLogin(backFromExternal = false) {
    this.store.resetAllClientRelatedInfo();

    if (backFromExternal) {
      this.#tvLoginStore.translationReset();
      this.resetLanguageAndLabelsToDefault();
    }

    this.languageService.setClientLanguages([]);
    this.#tvLoginStore.resetUrlParameterClientAlias();

    return this.router.navigate([RouterStates.login]);
  }

  backToLoginFromExternal(): void {
    const clientAlias = this.store.isClientDefined() ? this.store.client.alias : null;

    if (!clientAlias) {
      this.goToLogin(true);
    } else {
      this.goToClient(clientAlias);
    }
  }

  goToLandingPage() {
    const returnUrl = this.#tvLoginStore.getUrlParameterReturnUrl();

    if (!returnUrl) {
      window.location.href = this.MAIN_APP_URL;
      return;
    } else {
      var decodedReturnUrl =
        returnUrl.toLowerCase().indexOf('&relaystate=') > -1 ? returnUrl : decodeURIComponent(returnUrl);
      window.location.href = this.isLocalUrl(decodedReturnUrl) ? decodedReturnUrl : this.MAIN_APP_URL;
    }

    return false;
  }

  goToClient(clientName: string) {
    return this.router.navigate([`${clientName}/${RouterStates.login}`]);
  }

  goToTermsPageWithSelectLanguage(loginResponse: LoginResponse) {
    if (this.languageService.isContainsClientLanguages(loginResponse.client.languageIds)) {
      return this.goToTermsAndConditions(loginResponse.client.name);
    }
    /** save response to the store to use it on select-language page */
    this.store.loginResponse = loginResponse;
    return this.goToSelectLanguage(() => this.goToTermsAndConditions(loginResponse.client.name));
  }

  goToSelectLanguage(goTo: Function) {
    this.store.goTo = goTo;
    return this.router.navigate([RouterStates.selectLanguage]);
  }

  goToTermsAndConditions(clientName: string, queryParams: Record<string, unknown> | null = null) {
    const url = `/${clientName}/${RouterStates.termsAndConditions}`;

    if (queryParams) {
      return this.router.navigate([url], { queryParams });
    }

    return this.router.navigate([url]);
  }

  redirectTo(url: string): void {
    window.location.href = url;
  }

  goToErrorPage(code: EErrorCode) {
    this.router.navigate([RouterStates.error, code]);
  }

  putClientToStore(client: ClientSSO, shouldRedirect = false): Promise<ClientSSO> {
    if (client) {
      this.store.client = new ClientSSO(client);

      if (shouldRedirect == false) {
        this.store.setFooterLogoStyle();
        this.store.setCustomCSS();
        this.store.setBackgroundImageStyle();
      }

      return Promise.resolve(this.store.client);
    }

    console.warn('en attempt to put client', client, ' to store');
    return Promise.reject(null);
  }

  ssoLogin(ssoUrl: string) {
    const returnUrl = this.#tvLoginStore.getUrlParameterReturnUrl();

    if (returnUrl) {
      ssoUrl = this.updateQueryStringParameter(ssoUrl, 'relaystate', encodeURIComponent(returnUrl));
    }
    ssoUrl = this.updateQueryStringParameter(ssoUrl, 'isMobile', this.mobileService.isMobileApp() ? 'true' : 'false');
    this.redirectTo(ssoUrl);
  }

  updateQueryStringParameter(uri, key, value) {
    const re = new RegExp('([?&])' + key + '=.*?(&|#|$)', 'i');
    if (uri.match(re)) {
      return uri.replace(re, '$1' + key + '=' + value + '$2');
    } else {
      let hash = '';
      if (uri.indexOf('#') !== -1) {
        hash = uri.replace(/.*#/, '#');
        uri = uri.replace(/#.*/, '');
      }
      const separator = uri.indexOf('?') !== -1 ? '&' : '?';
      return uri + separator + key + '=' + value + hash;
    }
  }

  urlIsNotForTermsAndConditions(): boolean {
    const url = window.location.href;
    return url && url.indexOf(RouterStates.termsAndConditions) === -1;
  }

  goToMonolith(): void {
    const clientAlias = this.store.getLoginAlias();
    const isMobileApp = this.mobileService.isMobileApp();

    if (isMobileApp && clientAlias) {
      const url = this.mobileService.getMobileRedirectUrl(clientAlias);
      this.redirectTo(url);

      return;
    }

    this.goToMonolithWithClientAlias(clientAlias);
  }

  goToMonolithWithClientAlias(clientAlias: string): void {
    const url = clientAlias ? `${this.MAIN_APP_URL}?clientAlias=${clientAlias}` : this.MAIN_APP_URL;
    this.redirectTo(url);
  }

  getClientAliasFromHash(urlHash: string): string {
    const regex = /#\/(.*?)\/login/;
    const match = urlHash.match(regex);

    return match && match[1] ? match[1] : '';
  }

  logoutByRedirect(clientId: string): void {
    const url = this.dictionaryAPIService.logoutUserFromTerms(clientId);
    this.redirectTo(url);
  }

  isRedirectAfterLogin(route: ActivatedRouteSnapshot | string): boolean {
    if (route instanceof ActivatedRouteSnapshot) {
      return route.queryParamMap.has(this.SSO_LOGIN_CALLBACK);
    }

    return route?.includes(this.SSO_LOGIN_CALLBACK);
  }

  needsMobileRedirect(redirectAfterLogin: boolean) {
    const isMobileApp = this.mobileService.isMobileApp();
    const mobileCallbackNotPresent = this.isMobileCallbackNotPresent();
    const notErrorRoute = this.isNotErrorRoute();

    return [isMobileApp, mobileCallbackNotPresent, !redirectAfterLogin, notErrorRoute].every(Boolean);
  }

  private resetLanguageAndLabelsToDefault() {
    this.appTranslateService.resetAllLanguages();

    if (!this.#tvLoginStore.getSelectedLanguageId()) {
      this.appTranslateService.setDefaultLanguage();
      this.languageService.setDefaultLanguage();
    }
  }

  private isLocalUrl(url: string): boolean {
    if (url == null) {
      return false;
    }

    if (url.startsWith('/')) {
      return true;
    }

    var regexp = new RegExp('^((?<protocol>[^/]*)//)?(?<hostname>[^:/]+)(:(?<port>[0-9]+)?)?.*$', 'i');
    var result = regexp.exec(url);
    if (result == null) {
      return false;
    }

    var groups = result['groups'];
    var protocol = groups['protocol'] ? groups['protocol'] : 'http:';
    var hostname = groups['hostname'];
    var port = groups['port'] ? groups['port'] : 80;

    return (
      window.location.protocol === protocol && window.location.hostname === hostname && window.location.port === port
    );
  }

  private onRouteChangeSuccess(event: NavigationEnd) {
    //console.log( 'onRouteChangeSuccess' );
    const key = 'General.PageTitle.TrueView';

    this.translate.get(key).subscribe((value) => {
      this.titleService.setTitle(value);
    });
  }

  isMobileCallbackNotPresent(): boolean {
    const REDIRECT_PARAM = 'redirect_uri';
    const domain = this.mobileService.getMobileRedirectDomain('callback');
    const redirect_uri = `${REDIRECT_PARAM}=${domain}`.toLowerCase();
    const returnUrl = this.#tvLoginStore.getUrlParameterReturnUrl();

    return decodeURIComponent(returnUrl)
      .split('&')
      .filter((param) => param.includes(REDIRECT_PARAM))
      .every((param) => param.toLowerCase() !== redirect_uri);
  }

  isNotErrorRoute(): boolean {
    return !location.href.includes(`/${RouterStates.error}/`);
  }

  goSelectPortal(): Promise<boolean> {
    return this.router.navigate([RouterStates.selectPortal]);
  }
}
